<template>
  <div class="municipal-series" v-loading="loading">
    <div class="series-toolbar">
      <span>查看项目</span>
      <CSelect
        class="project-select"
        :options="projectOptions"
        :default="assetFilter"
        :clearable="false"
        :middle="true"
        @CSelect_select_val="assetFilter = $event" />
    </div>
    <section v-for="panel in visiblePanels" :key="panel.reit_code" class="series-card">
      <div class="series-title">
        <div><b>{{ panel.name }}</b><span>{{ panel.reit_code }}</span></div>
        <small>{{ panel.business_groups && panel.business_groups.length ? '分业务运营指标' : '量 / 价 / 质量' }}</small>
      </div>
      <template v-if="panel.business_groups && panel.business_groups.length">
        <div class="business-chart-grid">
          <article v-for="group in panel.business_groups" :key="group.name" class="business-chart-card">
            <h4>{{ group.name }}</h4>
            <div :ref="`chart_${chartKey(panel.reit_code, group.name)}`" class="series-chart compact"></div>
            <div class="series-table-wrap">
              <el-table :data="group.metrics" class="municipal-detail-table">
                <el-table-column label="指标 / 单位" min-width="230" fixed>
                  <template slot-scope="scope">
                    <span>
                      <i :style="{ background: metricColor(scope.row.role) }"></i>
                      <el-tooltip
                        :content="scope.row.cn_name + (scope.row.unit ? '（' + scope.row.unit + '）' : '')"
                        placement="top"
                        :disabled="!isLongText(scope.row.cn_name)"
                        :show-after="300">
                        <span class="metric-name-cell">
                          {{ scope.row.cn_name }}<em>{{ scope.row.unit }}</em>
                        </span>
                      </el-tooltip>
                      <span class="metric-controls">
                        <CSelect
                          :options="chartTypeOptions"
                          :default="chartType(panel.reit_code, scope.row)"
                          :clearable="false"
                          :popperAppendToBody="true"
                          custWidth="58px"
                          @CSelect_select_val="setChartType(panel.reit_code, scope.row, $event)" />
                        <CSelect
                          :options="axisOptions"
                          :default="axisChoice(panel.reit_code, scope.row)"
                          :clearable="false"
                          :popperAppendToBody="true"
                          custWidth="58px"
                          @CSelect_select_val="setAxisChoice(panel.reit_code, scope.row, $event)" />
                      </span>
                    </span>
                  </template>
                </el-table-column>
                <el-table-column
                  v-for="period in periodsFromMetrics(group.metrics)"
                  :key="period"
                  :label="period"
                  min-width="118"
                  align="right">
                  <template slot-scope="scope">
                    {{ formatValue(pointFor(scope.row, period).value, scope.row.unit) }}
                    <i
                      v-if="hasValue(pointFor(scope.row, period).value)"
                      class="el-icon-search source-icon"
                      title="查看该期财报追源"
                      @click.stop="openSource(panel, scope.row, pointFor(scope.row, period))"></i>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </article>
        </div>
      </template>
      <template v-else>
        <div :ref="`chart_${chartKey(panel.reit_code)}`" class="series-chart"></div>
        <div class="series-table-wrap">
          <el-table :data="panel.metrics" class="municipal-detail-table">
            <el-table-column label="指标 / 单位" min-width="330" fixed>
              <template slot-scope="scope">
                <span>
                <i :style="{ background: metricColor(scope.row.role) }"></i>
                <el-tooltip
                  :content="scope.row.cn_name + (scope.row.unit ? '（' + scope.row.unit + '）' : '')"
                  placement="top"
                  :disabled="!isLongText(scope.row.cn_name)"
                  :show-after="300">
                  <span class="metric-name-cell">{{ scope.row.cn_name }}<em>{{ scope.row.unit }}</em></span>
                </el-tooltip>
                <span class="metric-controls">
                  <CSelect
                    :options="chartTypeOptions"
                    :default="chartType(panel.reit_code, scope.row)"
                    :clearable="false"
                    :popperAppendToBody="true"
                    custWidth="58px"
                    @CSelect_select_val="setChartType(panel.reit_code, scope.row, $event)" />
                  <CSelect
                    :options="axisOptions"
                    :default="axisChoice(panel.reit_code, scope.row)"
                    :clearable="false"
                    :popperAppendToBody="true"
                    custWidth="58px"
                    @CSelect_select_val="setAxisChoice(panel.reit_code, scope.row, $event)" />
                </span>
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="period in periodsFromMetrics(panel.metrics)"
              :key="period"
              :label="period"
              min-width="118"
              align="right">
              <template slot-scope="scope">
                {{ formatValue(pointFor(scope.row, period).value, scope.row.unit) }}
                <i
                  v-if="hasValue(pointFor(scope.row, period).value)"
                  class="el-icon-search source-icon"
                  title="查看该期财报追源"
                  @click.stop="openSource(panel, scope.row, pointFor(scope.row, period))"></i>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import CSelect from '@/components/Basic/CSelect.vue';

export default {
  name: 'MunicipalEcoTimeseries',
  components: { CSelect },
  props: { codes: { type: Array, default: () => [] } },
  data() {
    return {
      loading: false,
      panels: [],
      assetFilter: 'all',
      charts: {},
      chartPrefs: {},
      loadSequence: 0,
      chartTypeOptions: [{ label: '折线', value: 'line' }, { label: '柱状', value: 'bar' }],
      axisOptions: [{ label: '左轴', value: 'left' }, { label: '右轴', value: 'right' }],
    };
  },
  computed: {
    visiblePanels() {
      return this.assetFilter === 'all' ? this.panels : this.panels.filter((p) => p.reit_code === this.assetFilter);
    },
    projectOptions() {
      return [
        { label: '全部项目（顺序展示）', value: 'all' },
        ...this.panels.map((panel) => ({ label: panel.name, value: panel.reit_code })),
      ];
    },
    chartSections() {
      return this.visiblePanels.flatMap((panel) => {
        if (panel.business_groups && panel.business_groups.length) {
          return panel.business_groups.map((group) => ({
            key: this.chartKey(panel.reit_code, group.name),
            code: panel.reit_code,
            metrics: (group.metrics || []).map((metric, index) => ({
              ...metric,
              role: metric.role || (index === 0 ? 'vol_main' : (/单价/.test(metric.cn_name) ? 'avg_price' : 'equip_util')),
            })),
          }));
        }
        return [{ key: this.chartKey(panel.reit_code), code: panel.reit_code, metrics: panel.metrics || [] }];
      });
    },
  },
  watch: {
    codes: { deep: true, handler() { this.load(); } },
    assetFilter() { this.$nextTick(this.renderCharts); },
  },
  mounted() {
    this.load();
    window.addEventListener('resize', this.resizeCharts);
    // 延迟一帧确保 v-for 出来的 chart refs 全部挂上，再开始渲染
    requestAnimationFrame(() => {
      if (this.panels.length) this.renderCharts();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts);
    Object.values(this.charts).forEach((chart) => chart && chart.dispose());
  },
  methods: {
    async load() {
      const requestedCodes = [...this.codes];
      const sequence = ++this.loadSequence;
      if (!requestedCodes.length) {
        this.panels = [];
        return;
      }
      this.loading = true;
      try {
        const qs = new URLSearchParams({
          category_2: '市政生态',
          period_type: 'Q',
          current_period: '',
          full_history: 'true',
        });
        requestedCodes.forEach((code) => qs.append('codes', code));
        const { data } = await this.$https.get(`/api/v4/get_expressway_chart_data?${qs}`);
        // 网络较慢时旧请求可能晚于新请求返回，不能让旧的少代码响应覆盖完整列表。
        if (sequence !== this.loadSequence) return;
        if (data.code === 200) {
          this.panels = data.data.panels || [];
          if (this.assetFilter !== 'all' && !this.panels.some((panel) => panel.reit_code === this.assetFilter)) {
            this.assetFilter = 'all';
          }
        }
      } finally {
        if (sequence === this.loadSequence) {
          this.loading = false;
          // 用 RAF 让 v-for chart refs 完成挂载后再渲染，避免批量 init 时 dom 还没好
          requestAnimationFrame(() => this.renderCharts());
        }
      }
    },
    chartKey(code, suffix = 'main') { return `${code}_${String(suffix).replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_')}`; },
    periods(panel) { return this.periodsFromMetrics(panel.metrics); },
    periodsFromMetrics(metrics = []) {
      const rows = metrics || [];
      const timeline = ((rows[0] || {}).data || []);
      if (!timeline.length) return [];
      // 每只券从首个真实运营披露期开始。价格可能是政策常量，不能凭价格
      // 在上市前制造历史，因此优先用量/质量指标确定起点。
      const operational = rows.filter((metric) => metric.role !== 'avg_price');
      const anchors = operational.length ? operational : rows;
      const first = timeline.findIndex((_point, index) => anchors.some((metric) => {
        const point = (metric.data || [])[index];
        return point && this.hasValue(point.value);
      }));
      return timeline.slice(first < 0 ? 0 : first).map((p) => `${p.name}${p.tag}`);
    },
    pointFor(metric, period) {
      return (metric.data || []).find((point) => `${point.name}${point.tag}` === period) || {
        name: String(period).slice(0, 4),
        tag: String(period).slice(4),
        value: null,
      };
    },
    hasValue(value) { return value !== null && value !== undefined && value !== ''; },
    isLongText(text) {
      // 超过 12 个字符或含特殊长内容时启用省略 + tooltip
      if (!text) return false;
      return String(text).length > 12 || /[一-龥]{8,}/.test(text);
    },
    openSource(panel, metric, point) {
      if (!point || !this.hasValue(point.value)) return this.$message.warning('当前指标暂无可追源数据');
      this.$emit('open-source', {
        code: panel.reit_code,
        period: `${point.name}${point.tag}`,
        indicator: metric.cn_name,
      });
    },
    metricColor(role) { return { vol_main: '#5ae8ed', avg_price: '#f5a95f', equip_util: '#c8ff00' }[role] || '#6da8ff'; },
    formatValue(value, unit) {
      if (value === null || value === undefined || value === '') return '—';
      if (unit === '%' || unit === 'pct') return `${(Number(value) * 100).toFixed(2)}%`;
      return Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 2 });
    },
    axisSide(metric) { return metric.role === 'vol_main' ? 'left' : 'right'; },
    prefKey(code, metric) { return `${code}:${metric.field}`; },
    chartType(code, metric) {
      const pref = this.chartPrefs[this.prefKey(code, metric)] || {};
      return pref.type || (metric.role === 'vol_main' ? 'bar' : 'line');
    },
    axisChoice(code, metric) {
      const pref = this.chartPrefs[this.prefKey(code, metric)] || {};
      return pref.axis || this.axisSide(metric);
    },
    setChartType(code, metric, type) {
      const key = this.prefKey(code, metric);
      this.$set(this.chartPrefs, key, { ...(this.chartPrefs[key] || {}), type });
      this.renderCharts();
    },
    setAxisChoice(code, metric, axis) {
      const key = this.prefKey(code, metric);
      this.$set(this.chartPrefs, key, { ...(this.chartPrefs[key] || {}), axis });
      this.renderCharts();
    },
    renderCharts() {
      const visibleKeys = new Set(this.chartSections.map((section) => section.key));
      Object.keys(this.charts).forEach((key) => {
        if (!visibleKeys.has(key)) {
          this.charts[key].dispose();
          this.$delete(this.charts, key);
        }
      });
      // 大 panel 时 el-table 列数极多，同步 init 会卡住主线程。先做"首屏优先"：
      // 第一批只渲染前 2 个 panel，剩余的等用户展开/滚动时再补
      const sections = this.chartSections.slice();
      const FIRST_BATCH_LIMIT = 8;
      const firstBatch = sections.slice(0, FIRST_BATCH_LIMIT);
      const restQueue = sections.slice(FIRST_BATCH_LIMIT);
      // 同步执行首屏（保证用户能看到东西）
      firstBatch.forEach((s) => this.renderOne(s));
      // 剩余分批 RAF 后台补
      if (restQueue.length) {
        const BATCH = 2;
        let i = 0;
        const runBatch = () => {
          const end = Math.min(i + BATCH, restQueue.length);
          for (; i < end; i++) this.renderOne(restQueue[i]);
          if (i < restQueue.length) requestAnimationFrame(runBatch);
        };
        requestAnimationFrame(runBatch);
      }
    },
    renderOne(section) {
      const el = this.$refs[`chart_${section.key}`];
      const dom = Array.isArray(el) ? el[0] : el;
      if (!dom) return;
      let chart = this.charts[section.key];
      if (chart && chart.getDom() !== dom) {
        chart.dispose();
        chart = null;
      }
      chart = chart || this.$echarts.init(dom);
      this.charts[section.key] = chart;
      const metrics = section.metrics || [];
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 8, textStyle: { color: '#9298a1' } },
        grid: { left: 70, right: 70, top: 55, bottom: 38 },
        xAxis: {
          type: 'category',
          data: ((metrics[0] || {}).data || []).map((point) => `${point.name}${point.tag}`),
          axisLabel: {
            color: '#9298a1',
            rotate: 0,
            interval: 0,
            formatter: (value) => (String(value).endsWith('Q1') ? String(value).slice(0, 4) : ''),
          },
        },
        yAxis: [
          { type: 'value', position: 'left', scale: true, axisLabel: { color: '#9298a1' }, splitLine: { lineStyle: { color: '#34373b' } } },
          { type: 'value', position: 'right', scale: true, axisLabel: { color: '#9298a1' }, splitLine: { show: false } },
        ],
        series: metrics.map((metric) => ({
          name: `${metric.cn_name}（${metric.unit}）`,
          type: this.chartType(section.code, metric),
          yAxisIndex: this.axisChoice(section.code, metric) === 'left' ? 0 : 1,
          smooth: this.chartType(section.code, metric) === 'line',
          symbolSize: 6,
          barMaxWidth: 28,
          itemStyle: { color: this.metricColor(metric.role) },
          lineStyle: { color: this.metricColor(metric.role), width: 2 },
          data: (metric.data || []).map((point) => (point.value === '' ? null : point.value)),
        })),
      }, true);
      this.bindTraceClick(chart, section);
      chart.resize();
    },
    bindTraceClick(chart, section) {
      let pointerStart = null;
      const previous = chart.__municipalTraceHandlers;
      if (previous) {
        chart.getZr().off('mousedown', previous.mousedown);
        chart.off('click', previous.click);
      }
      const mousedown = (event) => {
        pointerStart = [event.offsetX, event.offsetY];
      };
      const click = (params) => {
        const nativeEvent = params.event || {};
        const pixel = [nativeEvent.offsetX, nativeEvent.offsetY];
        if (pointerStart && pixel.every((value) => Number.isFinite(value))) {
          const dragged = Math.abs(pixel[0] - pointerStart[0]) > 4 || Math.abs(pixel[1] - pointerStart[1]) > 4;
          if (dragged) return;
        }
        if (params.componentType !== 'series') return;
        const metric = (section.metrics || [])[params.seriesIndex];
        const point = metric && (metric.data || [])[params.dataIndex];
        if (!metric || !point || !this.hasValue(point.value)) return;
        this.openSource({ reit_code: section.code }, metric, point);
      };
      chart.__municipalTraceHandlers = { mousedown, click };
      chart.getZr().on('mousedown', mousedown);
      chart.on('click', click);
    },
    resizeCharts() { Object.values(this.charts).forEach((chart) => chart && chart.resize()); },
  },
};
</script>

<style scoped lang="less">
.municipal-series { width: 100%; padding: 0 20px 30px; color: var(--table_color); }
.series-toolbar { display:flex; align-items:center; justify-content:flex-start; gap:12px; margin:12px 0; color:var(--chart_title); }
.project-select { width:230px; margin-left:0; }
.series-card { margin:16px 0; padding:18px 20px 22px; border:1px solid var(--table_brc); border-radius:8px; background:var(--table_bgc); }
.series-title { display:flex; justify-content:space-between; border-left:4px solid #c8ff00; padding-left:12px; }
.series-title b { font-size:16px; } .series-title span,.series-title small { margin-left:10px; color:#9298a1; font-weight:normal; }
.series-chart { width:100%; height:350px; }
.series-chart.compact { height:280px; }
.series-table-wrap { overflow-x:auto; overflow-y:hidden; border:0; border-radius:0; background:var(--table_bgc); }
.series-table-wrap::-webkit-scrollbar { height:6px; }
.series-table-wrap::-webkit-scrollbar-thumb { background:var(--scrollbar-thumb, #6d6d6d); border-radius:3px; }
table { width:100%; table-layout:fixed; border-collapse:collapse; font-size:13px; background:var(--table_bgc); color:var(--table_color); }
th,td { padding:10px 8px; text-align:right; border-bottom:1px solid var(--table_brc); white-space:nowrap; background:var(--table_bgc); }
th { color:var(--table_th_color,var(--chart_title)); font-weight:500; background:var(--table_th_bgc,var(--table_bgc)); }
tbody tr:hover td { background:var(--table_bgc_hover); color:var(--table_color_hover); }
th:first-child,td:first-child { text-align:left; min-width:230px; } td:first-child i { display:inline-block; width:8px; height:8px; border-radius:50%; margin-right:8px; }
.metric-controls { display:inline-flex; gap:4px; margin-left:8px; vertical-align:middle; opacity:0.35; transition: opacity 0.18s ease; }
tr:hover .metric-controls, .metric-controls:focus-within { opacity:1; }
.metric-controls .container { width:64px !important; padding:0 !important; font-size:11px !important; }
.metric-controls ::v-deep .el-select { width:64px !important; height:26px !important; margin-left:0 !important; padding:0 6px !important; transition: border-color 0.15s ease; }
.metric-controls ::v-deep .el-select:hover { border-color:var(--primary-color) !important; }
.metric-controls ::v-deep .el-input,
.metric-controls ::v-deep .el-input__inner { height:26px !important; line-height:26px !important; padding:0 !important; font-size:11px !important; }
.metric-controls ::v-deep .el-input__suffix { right:0; }
::v-deep .el-select-dropdown {
  animation: municipal-fade-in 0.18s ease-out;
  border: 1px solid var(--table_brc) !important;
  border-radius: 4px !important;
}
::v-deep .el-select-dropdown__item {
  transition: background-color 0.12s ease, color 0.12s ease;
}
::v-deep .el-select-dropdown__item.hover,
::v-deep .el-select-dropdown__item:hover {
  background-color: rgba(90, 232, 237, 0.12) !important;
}
@keyframes municipal-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.metric-name-cell {
  position:relative;
  display:inline-block;
  max-width: 220px;
  vertical-align: middle;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}
.metric-name-cell em { color:#9298a1; font-style:normal; margin-left:6px; }
.value-with-source { display:inline-flex; align-items:center; justify-content:flex-end; gap:5px; }
.source-icon { color:var(--primary-color); cursor:pointer; font-size:12px !important; }
.source-icon:hover { color:#66b1ff; }
.municipal-detail-table { width:100%; background:var(--table_bgc); color:var(--table_color); }
::v-deep .municipal-detail-table th.el-table__cell,
::v-deep .municipal-detail-table td.el-table__cell { background:var(--table_bgc); color:var(--table_color); border-bottom-color:var(--table_brc); }
::v-deep .municipal-detail-table .el-table__fixed::before,
::v-deep .municipal-detail-table::before { background:transparent; }
td em { color:#9298a1; font-style:normal; margin-left:6px; } tbody tr:last-child td { border-bottom:0; }
.business-chart-grid { margin-top:14px; display:grid; grid-template-columns:1fr; gap:0; align-items:start; }
.business-chart-card { min-width:0; padding:14px 0 22px; border:0; border-bottom:1px solid var(--table_brc); border-radius:0; background:transparent; }
.business-chart-card:last-child { border-bottom:0; }
.business-chart-card h4 { margin:0 0 4px; padding-left:10px; border-left:3px solid var(--primary-color); color:var(--chart_title); font-size:14px; }
</style>
